<script setup lang="ts">import { computed as _computed } from 'vue';

import type { GiftlyDesign } from '@consumer/types/serializers'
import type { Photo } from '@consumer/types/Photo'
import { speedbumpText } from '@consumer/constants/disclaimers'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'

withDefaults(defineProps<{
  selected?: boolean
  photo: Photo | GiftlyDesign
}>(), {  })

const emit = defineEmits<{ select: [Photo | GiftlyDesign] }>()

const gift = _computed(() => __props.photo.provider === 'giftly' && ({
  giftlyMetadata: { ...__props.photo, designName: __props.photo.id },
  ...__props.photo,
}))

function getBackgroundImageUrl (photo: Photo) {
  if (photo.thumbnailUrl.indexOf('/') === 0) {
    return `${window.origin}${photo.thumbnailUrl}`
  }
  return photo.thumbnailUrl
}

function openPhotographerUrl () {
  // eslint-disable-next-line no-alert
  if ('photographerUrl' in __props.photo && confirm(speedbumpText)) {
    window.open(__props.photo.photographerUrl, '_blank')
  }
}

</script>

<template>
  <div
    class="relative group z-0"
    style="aspect-ratio: 360 / 240;"
    @click="emit('select', photo)"
  >
    <GiftlyV3Image
      v-if="gift"
      :gift="gift"
      class="clickable"
      name="nc_design_giftly"
    />
    <div
      v-else-if="photo.provider !== 'giftly'"
      class="rounded-md bg-cover bg-top clickable absolute inset-0"
      name="nc_design_photo"
      :style="{
        'background-image': `url('${getBackgroundImageUrl(photo)}')`,
      }"
      :alt="photo.id"
    />
    <div
      v-if="'photographerUrl' in photo"
      class="
      absolute bottom-0 w-full lg:hidden group-hover:block
      text-6px lg:text-xs text-white text-shadow-lg !leading-none
      bg-black bg-opacity-30 p-1
    "
    >
      <a
        target="_blank"
        :href="`${photo.photographerUrl}`"
        class="text-white"
        name="nc_unsplash_link"
        @click.prevent="openPhotographerUrl"
      >Photo by {{ photo.photographerName }}</a>
    </div>
    <div
      v-if="selected"
      class="absolute inset-0 border-1 border-solid border-primary-dark rounded-md
        bg-primary-base opacity-20 pointer-events-none
      "
    />
    <div
      v-if="selected"
      class="absolute top-2 right-2 bg-primary-base text-white h-5 w-5
        rounded-v2xs flex items-center justify-center"
    >
      <GIcon :icon="faCheck" size="sm"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
:deep(.giftly-image-v3-wrapper) {
  .giftly-image-v3 {
    border: none;
    border-radius: 0.375rem;
    box-shadow: none;
  }

  .giftly-image-v3--tagline {
    display: none;
  }
}
</style>
